<template>
  <v-form v-model="valid" ref="form">
    <div>
      <dashboard-header
        :text="item ? $t('role.updateRole') : $t('role.createRole')"
        :on-back="goBack"
      />

      <div class="d-flex justify-center align-center">
        <v-card
          elevation="0"
          class="card-border-10 w-75"
          :loading="loading"
        >
          <div class="pa-5">

            <!-- Role Information Card -->
            <v-card class="card-border-10 mb-6" outlined>
              <div class="custom-card-head d-flex p-3 text-18px">{{ $t('role.roleInformation') }}</div>
              <v-divider class="ma-0"></v-divider>

              <div class="pa-4">
                <!-- Default Role Info -->
                <v-alert
                  v-if="isDefaultRole"
                  type="info"
                  dense
                  outlined
                  class="mb-4"
                >
                  {{ $t('role.defaultRoleEditInfo') }}
                </v-alert>

                <!-- Role Name -->
                <div class="d-flex text-body-1 mb-2">
                  <div class="text-h6 mark-title me-2">*</div>
                  {{ $t('role.name') }}
                </div>
                <v-text-field
                  v-model="formData.name"
                  :placeholder="$t('role.namePlaceholder')"
                  :rules="isDefaultRole ? [] : roleNameValidation"
                  :disabled="isDefaultRole"
                  outlined
                  dense
                  counter="100"
                  class="mb-4"
                />

                <!-- Description -->
                <div class="d-flex text-body-1 mb-2">
                  <div class="text-h6 mark-title me-2">*</div>
                  {{ $t('role.description') }}
                </div>
                <v-textarea
                  v-model="formData.description"
                  :placeholder="$t('role.descriptionPlaceholder')"
                  :disabled="isDefaultRole"
                  outlined
                  dense
                  rows="3"
                  counter="500"
                  :rules="descriptionValidation"
                  class="mb-4"
                />

                <!-- Medical Staff Assignment Card -->
                <div class="d-flex text-body-1 mb-2">
                  {{ $t('role.employeeList') }}
                </div>
                <div class="mb-6">
                  <!-- Selected Staff Chips -->
                  <div v-if="selectedUsersData.length > 0">
                    <v-chip
                      v-for="user in selectedUsersData"
                      :key="user.employeeId"
                      class="custom-chip-employee ma-1"
                    >
                      {{ getUserDisplayName(user) }}
                      &nbsp;
                      <v-icon color="grey" @click="removeUser(user.employeeId)">mdi-close</v-icon>
                    </v-chip>
                    <v-divider style="margin-top: 4px; margin-bottom: 8px;"></v-divider>
                  </div>

                  <!-- Add Staff Button -->
                  <v-btn
                    color="primary"
                    @click="onClickAddStaff"
                    class="no-uppercase btn-custom-primary"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('role.addEmployee') }}
                  </v-btn>
              </div>

                <!-- Active Toggle -->
                <div class="d-flex align-center">
                  <v-switch
                    v-if="item"
                    inset
                    v-model="formData.active"
                    :disabled="isDefaultRole"
                    color="primary"
                  />
                  <div v-if="item" class="text-body-1">{{ $t('role.activeToggle') }}</div>
                </div>
              </div>
            </v-card>

            <!-- Permissions Setup Card -->
            <v-card v-if="!isDefaultRole" class="card-border-10 mb-6" outlined>
              <div class="custom-card-head d-flex p-3 text-18px">{{ $t('role.permissionSettings') }}</div>
              <v-divider class="ma-0"></v-divider>

              <div class="pa-4">
                <!-- Permission Groups Grid - Dynamic Rows -->
                <div v-for="(chunk, chunkIndex) in permissionGroupsChunks" :key="chunkIndex">
                  <v-row :class="chunkIndex > 0 ? 'mt-4' : ''">
                    <v-col
                      v-for="(group, groupIndex) in chunk"
                      :key="`${chunkIndex}-${groupIndex}`"
                      cols="12"
                      md="6"
                    >
                      <div class="permission-group-card pa-4" style="border: 1px solid #e0e0e0; border-radius: 8px; height: 320px; overflow-y: auto;">
                        <!-- Group Header with Select All -->
                        <div class="mb-1">
                          <v-checkbox
                            :input-value="isGroupFullySelected(group)"
                            :indeterminate="isGroupPartiallySelected(group)"
                            @change="toggleGroupPermissions(group)"
                            :label="group.name"
                            class="custom-checkbox mt-0 mb-0"
                            color="primary"
                          />
                        </div>

                        <!-- Individual Permissions -->
                        <div class="ml-8">
                          <v-checkbox
                            v-for="permission in group.permissions"
                            :key="permission.code"
                            v-model="selectedPermissions"
                            :value="permission.code"
                            :label="getPermissionLabel(permission)"
                            class="custom-checkbox mt-0"
                            dense
                          />
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-card>

            <!-- Action Buttons -->
            <div class="d-flex justify-space-between">
              <v-btn
                color="primary"
                :loading="saving"
                :disabled="!valid || (!isDefaultRole && selectedPermissions.length === 0)"
                @click="save"
              >
                {{ $t('common.save') }}
              </v-btn>
            </div>

          </div>
        </v-card>
      </div>
    </div>

    <!-- Staff Selector Dialog -->
    <v-dialog
      v-model="showStaffSelector"
      max-width="90%"
      persistent
    >
      <v-card class="card-border-20 pb-6">
        <v-card-title>
          <div class="w-100 d-flex align-center">
            <div class="text-h6">{{ $t('role.addMedicalStaff') }}</div>
            <v-btn
              class="ms-auto"
              icon
              @click="cancelStaffSelection"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>

        <v-divider class="ma-0" style="background-color: #F0F0F0;"></v-divider>


        <div class="px-6">
          <v-data-table
            hide-default-footer
            class="home-data-table mt-4 text-body-1 clickable-rows"
            show-select
            :page.sync="currentPage"
            :items-per-page="perPage"
            :single-select="false"
            v-model="tempSelectedUsersData"
            :loading="loadingUsers"
            :headers="staffTableHeaders"
            item-key="employeeId"
            :items="allUsersData"
            @click:row="toggleRowSelection"
          >
            <template v-slot:item.name="{ item }">
              <div class="ellipsis-cell" style="max-width: 220px;" :title="item.name">
                <span class="font-weight-bold">{{ item.name }}</span>
              </div>
            </template>

            <template v-slot:item.email="{ item }">
              <div class="ellipsis-cell" style="max-width: 220px;" :title="item.email">
                {{ item.email }}
              </div>
            </template>

            <template v-slot:item.phoneNumber="{ item }">
              <div>{{ item.phoneNumber }}</div>
            </template>

            <template v-slot:item.department="{ item }">
              <div class="ellipsis-cell" style="max-width: 220px;" :title="item.departmentInfo ? item.departmentInfo.name : ''">
                {{ item.departmentInfo ? item.departmentInfo.name : '' }}
              </div>
            </template>

            <template v-slot:item.title="{ item }">
              <div class="ellipsis-cell" style="max-width: 220px;" :title="item.titleInfo ? item.titleInfo.name : ''">
                {{ item.titleInfo ? item.titleInfo.name : '' }}
              </div>
            </template>

            <template slot="no-data">
              <div class="w-100 align-content-center" style="min-height: 300px">
                <div class="w-100 d-flex">
                  <img class="mx-auto" src="@/assets/images/ic-empty.svg" alt="empty"/>
                </div>
                <div class="text-center">{{ $t("base.noData") }}</div>
              </div>
            </template>
          </v-data-table>

          <div class="d-flex mt-6">
            <div class="ms-auto mt-3">{{ paginationCurrentText }}</div>
            <b-pagination
              class="custom-pagination"
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              aria-controls="staff-table"
            />
          </div>
        </div>

        <v-divider class="ma-0" style="background-color: #F0F0F0;"></v-divider>

        <div class="px-6">
          <div class="d-flex justify-end mt-4">
            <v-btn
              elevation="0"
              class="no-uppercase btn-custom-primary px-6"
              @click="confirmStaffSelection"
            >
              {{ $t('common.confirm') }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script>
import { get, post, put } from '@/api'
import { API2 } from '@/api'
import DashboardHeader from '../component/DashboardHeader'
import mixin from '../../mixin'

export default {
  name: 'RoleCreateOrUpdate',
  components: {
    DashboardHeader
  },
  mixins: [mixin],
  props: {
    openTab: {
      type: Function,
      required: true
    },
    item: {
      type: Object,
    },
  },
  data() {
    return {
      valid: false,
      loading: false,
      saving: false,
      loadingUsers: false,
      showStaffSelector: false,
      allPermissions: [],
      selectedPermissions: [],
      selectedUsers: [],
      selectedUsersData: [],
      allUsersData: [],
      tempSelectedUsers: [],
      tempSelectedUsersData: [],
      userList: [],
      expandedPanels: [],
      currentPage: 1,
      perPage: 10,
      formData: {
        name: '',
        description: '',
        active: true
      },
      rules: {
        required: value => !!value || this.$t('common.required'),
      }
    }
  },
  computed: {
    isDefaultRole() {
      return this.item && this.item.isDefault
    },
    roleNameValidation() {
      let name = this.formData.name
      return [
        name.length > 0 && name.length <= 100 || this.$t("ui.MSG0045"),
        name.trim().length > 0 || this.$t("ui.MSG0045")
      ];
    },
    descriptionValidation() {
      let description = this.formData.description
      return [
        description.length > 0 && description.length <= 500 || this.$t("ui.MSG0044"),
        description.trim().length > 0 || this.$t("ui.MSG0044")
      ];
    },
    permissionGroups() {
      // Helper function to sort permissions by action type
      const sortPermissions = (permissions) => {
        const actionOrder = {
          'view': 1,
          'create': 2,
          'update': 3,
          'delete': 4,
          'change_password': 5,
          'import': 5,
          'export': 6,
          'confirm': 7,
          'view_detail': 8,
          'update_own': 9
        }

        return permissions.sort((a, b) => {
          const actionA = a.code.split('.')[1] || ''
          const actionB = b.code.split('.')[1] || ''
          const orderA = actionOrder[actionA] || 99
          const orderB = actionOrder[actionB] || 99

          if (orderA !== orderB) {
            return orderA - orderB
          }

          // If same order, sort alphabetically
          return actionA.localeCompare(actionB)
        })
      }

      // Group permissions by permission code prefix (language-agnostic)
      const groups = [
        {
          name: this.$t('role.modules.hospital'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('hospital.')))
        },
        {
          name: this.$t('role.modules.sosType'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('sos_type.')))
        },
        {
          name: this.$t('role.modules.title'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('title.')))
        },
        {
          name: this.$t('role.modules.department'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('department.')))
        },
        {
          name: this.$t('role.modules.employee'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('employee.')))
        },
        {
          name: this.$t('role.modules.alertContent'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('alert_content.')))
        },
        {
          name: this.$t('role.modules.sosCase'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('sos_case.')))
        },
        {
          name: this.$t('role.modules.group'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('group.')))
        },
        {
          name: this.$t('role.modules.schedule'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('schedule.')))
        },
        {
          name: this.$t('role.modules.role'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('role.')))
        },
        {
          name: this.$t('role.modules.interHospital'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('inter_hospital.')))
        },
        {
          name: this.$t('role.modules.home'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('home.')))
        },
        {
          name: this.$t('role.modules.device'),
          permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('device.')))
        },
        // {
        //   name: this.$t('role.modules.account'),
        //   permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('account.')))
        // },
        // {
        //   name: this.$t('role.modules.setting'),
        //   permissions: sortPermissions(this.allPermissions.filter(p => p.code && p.code.startsWith('setting.')))
        // }
      ]

      // If code-based grouping doesn't work (fallback to module-based for backward compatibility)
      const codeGroupsWithData = groups.filter(g => g.permissions.length > 0)
      if (codeGroupsWithData.length === 0) {
        // Fallback to Vietnamese module names for backward compatibility
        const fallbackGroups = [
          {
            name: this.$t('role.modules.hospital'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Bệnh viện'))
          },
          {
            name: this.$t('role.modules.sosType'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Loại SOS'))
          },
          {
            name: this.$t('role.modules.title'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Chức danh'))
          },
          {
            name: this.$t('role.modules.department'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Khoa'))
          },
          {
            name: this.$t('role.modules.employee'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Nhân viên y tế'))
          },
          {
            name: this.$t('role.modules.alertContent'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Nội dung báo động'))
          },
          {
            name: this.$t('role.modules.sosCase'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Tạo báo động'))
          },
          {
            name: this.$t('role.modules.group'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Trực nhóm'))
          },
          {
            name: this.$t('role.modules.schedule'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Lịch trực'))
          },
          {
            name: this.$t('role.modules.role'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Phân quyền'))
          },
          {
            name: this.$t('role.modules.interHospital'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Tham gia liên viện'))
          },
          {
            name: this.$t('role.modules.home'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Trang chủ'))
          },
          {
            name: this.$t('role.modules.device'),
            permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Thiết bị báo động'))
          },
          // {
          //   name: this.$t('role.modules.account'),
          //   permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Tài khoản'))
          // },
          // {
          //   name: this.$t('role.modules.setting'),
          //   permissions: sortPermissions(this.allPermissions.filter(p => p.module === 'Cài đặt'))
          // }
        ]
        return fallbackGroups.filter(g => g.permissions.length > 0)
      }

      return codeGroupsWithData
    },
    permissionGroupsChunks() {
      // Split permission groups into chunks of 2 for better viewing
      const chunks = []
      const groups = this.permissionGroups
      for (let i = 0; i < groups.length; i += 2) {
        chunks.push(groups.slice(i, i + 2))
      }
      return chunks
    },
    staffTableHeaders() {
      return [
        { value: "name", text: this.$t("employee.name") },
        { value: "email", text: this.$t("ui.email") },
        { value: "phoneNumber", text: this.$t("ui.phoneNumber") },
        { value: "department", text: this.$t("employee.department") },
        { value: "title", text: this.$t("employee.title") },
      ];
    },
    totalRows() {
      return this.allUsersData.length
    },
    paginationCurrentText() {
      let last = this.currentPage * this.perPage
      let first = last - this.perPage + 1
      let total = this.totalRows
      last = last > total ? total : last
      return `${first} - ${last} / ${total}`
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async onTab() {
      // Called when this tab is activated
      this.init()
    },

    async init() {
      // Load all permissions and user data first
      await Promise.all([
        this.loadPermissions(),
        this.loadAllUsersData()
      ])

      // Initialize form data
      if (this.item) {
        // Load full role details if editing
        await this.loadRoleDetails()
      } else {
        this.formData = {
          name: '',
          description: '',
          active: true
        }
        this.selectedPermissions = []
        this.selectedUsers = []
      }

      // Expand all panels by default
      this.expandedPanels = this.permissionGroups.map((_, index) => index)
    },

    async loadPermissions() {
      try {
        const response = await get(this, API2.PERMISSIONS)
        this.userList = await get(this, API2.EMPLOYEE, { page: 1, itemsPerPage: 1000, includeKhoa: 'false' })

        if (response && response.data) {
          // Support both old format (code: 0) and new format (status: 200)
          const isSuccess = response.data.status === 200 || response.data.code === 0

          if (isSuccess) {
            this.allPermissions = response.data.data || []
          }
        }
      } catch (error) {
        // Error loading permissions
      }
    },

    async loadRoleDetails() {
      try {
        if (!this.item || !this.item.id) return

        // Load role details with permissions
        const response = await get(this, `${API2.ROLE}/${this.item.id}`)

        if (response && response.data && (response.data.status === 200 || response.data.code === 0)) {
          const roleData = response.data.data || response.data

          this.formData = {
            name: roleData.name,
            description: roleData.description || '',
            active: roleData.active !== false
          }

          // Convert array of permission objects to array of permission codes
          this.selectedPermissions = roleData.permissions ?
            roleData.permissions.map(p => typeof p === 'string' ? p : p.code) : []

          // Load users assigned to this role
          await this.loadRoleUsers()
          this.updateSelectedUsersData()
        }
      } catch (error) {
        this.showToast('error', this.$t('common.loadError'))
      }
    },

    async loadRoleUsers() {
      try {
        if (!this.item || !this.item.id) return

        const response = await get(this, `${API2.ROLE}/${this.item.id}/users`)

        if (response && response.data && (response.data.status === 200 || response.data.code === 0)) {
          const users = response.data.data || []
          // Filter only EMPLOYEE users and extract their IDs
          this.selectedUsers = users
            .filter(user => user.userType === 'EMPLOYEE')
            .map(user => user.employeeId)
        }
      } catch (error) {
        // Error loading role users
      }
    },

    async loadAllUsersData() {
      try {
        const response = await get(this, API2.EMPLOYEE, { page: 1, itemsPerPage: 1000, includeKhoa: 'false' })

        if (response && response.data && (response.data.code === 0 || response.data.status === 200)) {
          const responseData = response.data.data || response.data
          this.allUsersData = responseData.data || responseData || []
        }
      } catch (error) {
        // Error loading all users data
      }
    },

    updateSelectedUsersData() {
      this.selectedUsersData = this.allUsersData.filter(user => {
        const userId = user.employeeId || user.id
        // Convert both to same type for comparison
        return this.selectedUsers.some(selectedId =>
          String(selectedId) === String(userId) || Number(selectedId) === Number(userId)
        )
      })?.sort((a,b) => a?.displayName?.localeCompare(b?.displayName))
    },

    removeUser(employeeId) {
      this.selectedUsers = this.selectedUsers.filter(id => id !== employeeId)
      this.updateSelectedUsersData()
    },

    onClickAddStaff() {
      if(this.userList?.data?.data?.data?.length > 0) {
        this.showStaffSelector = true
      } else {
        this.notifDialog({
          title: `${this.$t("warning.warning")}!`,
          content: this.$t("ui.medicalStaffListIsEmpty"),
          textConfirm: this.$t('ui.confirm'),
        });
        return;
      }
    },

    cancelStaffSelection() {
      this.showStaffSelector = false
      this.tempSelectedUsers = []
      this.tempSelectedUsersData = []
    },

    confirmStaffSelection() {
      // Get employee IDs from selected users data
      const newUserIds = this.tempSelectedUsersData.map(user => user.employeeId)

      // Add new selected users (avoiding duplicates)
      const newUsers = newUserIds.filter(id =>
        !this.selectedUsers.includes(id)
      )
      this.selectedUsers = [...this.selectedUsers, ...newUsers]
      this.updateSelectedUsersData()

      this.showStaffSelector = false
      this.tempSelectedUsers = []
      this.tempSelectedUsersData = []
    },

    toggleRowSelection(item) {
      // Check if item is already selected
      const isSelected = this.tempSelectedUsersData.some(user => user.employeeId === item.employeeId)

      if (isSelected) {
        // Remove from selection
        this.tempSelectedUsersData = this.tempSelectedUsersData.filter(user => user.employeeId !== item.employeeId)
      } else {
        // Add to selection
        this.tempSelectedUsersData = [...this.tempSelectedUsersData, item]
      }
    },

    getUserDisplayName(user) {
      // For employees with titleId 10 (khoa accounts), use displayName if available
      if (user.titleId === 10 && user.displayName) {
        return user.displayName
      }
      return user.name || user.email
    },

    isGroupFullySelected(group) {
      return group.permissions.length > 0 &&
             group.permissions.every(p => this.selectedPermissions.includes(p.code))
    },

    isGroupPartiallySelected(group) {
      const selectedCount = group.permissions.filter(p =>
        this.selectedPermissions.includes(p.code)
      ).length
      return selectedCount > 0 && selectedCount < group.permissions.length
    },

    toggleGroupPermissions(group) {
      const isFullySelected = this.isGroupFullySelected(group)

      if (isFullySelected) {
        // Remove all permissions from this group
        group.permissions.forEach(permission => {
          const index = this.selectedPermissions.indexOf(permission.code)
          if (index > -1) {
            this.selectedPermissions.splice(index, 1)
          }
        })
      } else {
        // Add all permissions from this group
        group.permissions.forEach(permission => {
          if (!this.selectedPermissions.includes(permission.code)) {
            this.selectedPermissions.push(permission.code)
          }
        })
      }
    },

    getPermissionLabel(permission) {
      // Get permission code - try different possible field names
      const permissionCode = permission.code || permission.permission_code || permission.name || permission.key

      if (permissionCode) {
        // Try to get translated label first
        const translationKey = `role.permissionActions.${permissionCode}`
        const translated = this.$t(translationKey)

        // If translation exists (not the same as key), use it
        if (translated !== translationKey) {
          return translated
        }
      }

      // Fallback to action, description, or code
      return permission.action || permission.description || permissionCode || 'Unknown permission'
    },

    async save() {
      if (!this.valid) return

      this.saving = true
      try {
        if (this.item) {
          // Update existing role
          await this.updateRoleWithUsers()
        } else {
          // Create new role
          await this.createRoleWithUsers()
        }
      } finally {
        this.saving = false
      }
    },

    async createRoleWithUsers() {
      try {
        // Step 1: Create the role
        const roleData = {
          name: this.formData.name.replace(/\s+/g, " ").trim(),
          description: this.formData.description.replace(/\s+/g, " ").trim(),
          active: this.formData.active,
          permissions: this.selectedPermissions
        }
        const response = await post(this, API2.ROLE, roleData)

        if (response && response.data && (response.data.code === 0 || response.data.status === 200)) {
          const createdRole = response.data.data || response.data
          const roleId = createdRole.id

          // Step 2: Assign selected users to the new role (if any)
          if (this.selectedUsers.length > 0) {
            await this.assignUsersToRole(roleId, this.selectedUsers)
          }

          this.showToast('success', this.$t('role.createSuccess'))
          this.goBack()
        } else {
          this.showToast('error', response?.data?.message || this.$t('role.createError'))
        }
      } catch (error) {
        this.showToast('error', this.$t('role.createError'))
      }
    },

    async updateRoleWithUsers() {
      let roleUpdateSuccess = false
      let roleUpdateError = null

      try {
        // Step 1: Try to update the role (may fail for default roles)
        const roleData = {
          name: this.formData.name.replace(/\s+/g, " ").trim(),
          description: this.formData.description.replace(/\s+/g, " ").trim(),
          active: this.formData.active,
          permissions: this.selectedPermissions
        }
        const response = await put(this, `${API2.ROLE}/${this.item.id}`, roleData)

        if (response && response.data && (response.data.code === 0 || response.data.status === 200)) {
          roleUpdateSuccess = true
        } else if (response && response.data && response.data.status === 403) {
          // Handle 403 error for default roles - show server message but continue
          roleUpdateError = response.data.error?.message || response.data.message
        } else {
          throw new Error(response?.data?.message || this.$t('common.saveError'))
        }
      } catch (error) {
        if (error.response && error.response.status === 403) {
          // Handle 403 error for default roles - show server message
          roleUpdateError = error.response.data?.error?.message || error.response.data?.message
        } else {
          this.showToast('error', error.message || this.$t('common.saveError'))
          return
        }
      }

      try {
        // Step 2: Always try to handle user assignment changes
        await this.handleUserAssignmentChanges()

        // Always show success message - user assignment was updated
        this.showToast('success', this.$t('role.updateSuccess'))

        this.goBack()
      } catch (userError) {
        if (roleUpdateSuccess) {
          this.showToast('warning', this.$t('role.updateSuccess') + ' ' + this.$t('role.userAssignmentPartialError'))
        } else if (roleUpdateError) {
          this.showToast('error', roleUpdateError + '. ' + this.$t('role.userAssignmentError'))
        } else {
          this.showToast('error', this.$t('role.userAssignmentError'))
        }
      }
    },

    async handleUserAssignmentChanges() {
      try {
        // Get current users assigned to role
        const currentResponse = await get(this, `${API2.ROLE}/${this.item.id}/users`)
        let currentUserIds = []

        if (currentResponse && currentResponse.data && (currentResponse.data.code === 0 || currentResponse.data.status === 200)) {
          const currentUsers = currentResponse.data.data || []
          currentUserIds = currentUsers
            .filter(user => user.userType === 'EMPLOYEE')
            .map(user => user.employeeId)
        }
        const newUserIds = this.selectedUsers || []

        // Calculate differences
        const usersToAdd = newUserIds.filter(id => !currentUserIds.includes(id))
        const usersToRemove = currentUserIds.filter(id => !newUserIds.includes(id))

        // Add new users
        if (usersToAdd.length > 0) {
          await this.assignUsersToRole(this.item.id, usersToAdd)
        }

        // Remove users
        if (usersToRemove.length > 0) {
          await this.removeUsersFromRole(this.item.id, usersToRemove)
        }
      } catch (error) {
        this.showToast('warning', this.$t('role.userAssignmentPartialError'))
      }
    },

    async assignUsersToRole(roleId, userIds) {
      try {
        // Use existing ROLE_ASSIGN API to assign each user
        const assignPromises = userIds.map(userId =>
          post(this, API2.ROLE_ASSIGN, {
            roleId,
            userId,
            userType: 'EMPLOYEE'
          })
        )

        await Promise.all(assignPromises)
      } catch (error) {
        this.showToast('warning', this.$t('role.userAssignmentPartialError'))
      }
    },

    async removeUsersFromRole(roleId, userIds) {
      try {
        // Use existing ROLE_REMOVE API to remove each user
        const removePromises = userIds.map(userId =>
          post(this, API2.ROLE_REMOVE, {
            roleId,
            userId,
            userType: 'EMPLOYEE'
          })
        )

        await Promise.all(removePromises)
      } catch (error) {
        this.showToast('warning', this.$t('role.userAssignmentPartialError'))
      }
    },

    goBack() {
      this.openTab(0)
      this.selectedUsersData = []
    }
  }
}
</script>

<style scoped>
.permission-group-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.permission-group-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.mark-title {
  color: red;
}

.custom-card-head {
  background-color: #f5f5f5;
  font-weight: 500;
}

.custom-chip-employee {
  border: 1px solid #D9D9D9 !important;
  border-radius: 2px;
  background-color: #FAFAFA !important;
}

.ellipsis-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


.custom-checkbox >>> .v-input__slot .v-label {
  margin-top: 8px !important;
}

.custom-pagination >>> .page-item {
  margin: 5px;
}

.custom-pagination >>> .page-item .page-link {
  color: #000000;
}

.custom-pagination >>> .active > .page-link {
  background: unset;
  color: #1890FF;
  border: 1px solid #1890FF;
}

.custom-pagination >>> .disabled > .page-link {
  color: #D9D9D9;
}

.clickable-rows >>> tbody tr {
  cursor: pointer;
}

.clickable-rows >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.clickable-rows >>> tbody tr.v-data-table__selected {
  background-color: #e3f2fd !important;
}

.clickable-rows >>> tbody tr.v-data-table__selected:hover {
  background-color: #bbdefb !important;
}

</style>